import React from 'react';
import {Box, Button, Paper, Stack, Typography, Tabs, Tab, TextField} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {SnackbarField, TransferTreeList} from '@/components/common';
import * as FsCategoryService from '@/services/accountService/fsCategoryService';
import {AuditCompanyFSCategoryService} from '@/services/auditService';
import {
    getCustomNodeQuota,
    saveCustomNodeQuota,
} from '@/services/auditService/auditCompanyFSCustomNodeService';

interface CompanyFSCategoryOperaProps {
    companyId: number;
    companyName?: string;
    bsTree: FsCategoryService.FsCategory[];
    plTree: FsCategoryService.FsCategory[];
}

type TransferNode = {
    id: number;
    label: React.ReactNode;
    children?: TransferNode[];
};

const mapFsTreeToTransfer = (nodes: FsCategoryService.FsCategory[] | undefined): TransferNode[] => {
    if (!nodes) return [];
    return nodes.map(n => ({
        id: n.fsId as number,
        label: (n.nameEn || n.nameZh || '') as React.ReactNode,
        children: mapFsTreeToTransfer(n.children),
    }));
};

const CompanyFSCategoryOpera: React.FC<CompanyFSCategoryOperaProps> = ({companyId, bsTree, plTree}) => {
    const theme = useTheme();
    const [tab, setTab] = React.useState<'BS' | 'PL'>('BS');
    const [fsTreeBS, setFsTreeBS] = React.useState<FsCategoryService.FsCategory[]>(bsTree || []);
    const [fsTreePL, setFsTreePL] = React.useState<FsCategoryService.FsCategory[]>(plTree || []);
    const [selectedFsIdsBS, setSelectedFsIdsBS] = React.useState<Array<number>>([]);
    const [selectedFsIdsPL, setSelectedFsIdsPL] = React.useState<Array<number>>([]);
    const [transferHeight, setTransferHeight] = React.useState(360);
    const transferHostRef = React.useRef<HTMLDivElement | null>(null);

    const [loading, setLoading] = React.useState(false);
    const [snackbar, setSnackbar] = React.useState<{
        open: boolean;
        message?: string;
        severity?: 'success' | 'error'
    }>({open: false});
    /** Admin: max custom Level-3 nodes under PL-COT/OI/AE (default 5) */
    const [customNodeQuota, setCustomNodeQuota] = React.useState<number>(5);
    const [quotaSaving, setQuotaSaving] = React.useState(false);

    React.useEffect(() => { setFsTreeBS(bsTree || []); }, [bsTree]);
    React.useEffect(() => { setFsTreePL(plTree || []); }, [plTree]);

    React.useEffect(() => {
        if (!companyId) {
            setCustomNodeQuota(5);
            return;
        }
        let cancelled = false;
        getCustomNodeQuota(Number(companyId))
            .then((n) => {
                if (!cancelled) setCustomNodeQuota(Number(n) > 0 ? Number(n) : 5);
            })
            .catch(() => {
                if (!cancelled) setCustomNodeQuota(5);
            });
        return () => { cancelled = true; };
    }, [companyId]);

    // Dynamic height: fill remaining viewport space under tabs / validation line
    React.useEffect(() => {
        const el = transferHostRef.current;
        if (!el) return;
        const measure = () => {
            const h = Math.floor(el.getBoundingClientRect().height);
            setTransferHeight(Math.max(200, h - 8));
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [tab]);

    const flattenFsIds = React.useCallback((nodes: FsCategoryService.FsCategory[] | undefined): number[] => {
        const out: number[] = [];
        const walk = (arr: FsCategoryService.FsCategory[] | undefined) => {
            if (!arr) return;
            for (const n of arr) {
                if (n.fsId != null) out.push(Number(n.fsId));
                if (n.children?.length) walk(n.children);
            }
        };
        walk(nodes);
        return out;
    }, []);

    React.useEffect(() => {
        const prefill = async () => {
            if (!companyId) {
                setSelectedFsIdsBS([]);
                setSelectedFsIdsPL([]);
                return;
            }
            try {
                const companyFsIds = await AuditCompanyFSCategoryService.queryFsIdsByCompanyId(Number(companyId));
                const setBS = new Set<number>(flattenFsIds(fsTreeBS));
                const setPL = new Set<number>(flattenFsIds(fsTreePL));
                const arr = (companyFsIds || []).map(Number);
                setSelectedFsIdsBS(arr.filter(id => setBS.has(id)));
                setSelectedFsIdsPL(arr.filter(id => setPL.has(id)));
            } catch {
                // ignore
            }
        };
        prefill();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, fsTreeBS, fsTreePL, flattenFsIds]);

    const handleSaveQuota = async () => {
        if (!companyId || !Number.isFinite(companyId)) {
            setSnackbar({open: true, severity: 'error', message: 'Company is require not null!'});
            return;
        }
        const max = Math.max(1, Math.floor(Number(customNodeQuota) || 5));
        try {
            setQuotaSaving(true);
            const saved = await saveCustomNodeQuota(Number(companyId), max);
            setCustomNodeQuota(Number(saved) > 0 ? Number(saved) : max);
            setSnackbar({open: true, severity: 'success', message: `Custom node limit saved: ${saved}`});
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Save quota failed';
            setSnackbar({open: true, severity: 'error', message: msg});
        } finally {
            setQuotaSaving(false);
        }
    };

    const handleSave = async () => {
        if (!companyId || !Number.isFinite(companyId)) {
            setSnackbar({open: true, severity: 'error', message: 'Company is require not null!'});
            return;
        }
        const currStatement: 'BS' | 'PL' = tab;
        const currSelected = currStatement === 'BS' ? selectedFsIdsBS : selectedFsIdsPL;
        if (!currSelected || currSelected.length === 0) {
            setSnackbar({open: true, severity: 'error', message: currStatement + ' statement is require not null!'});
            return;
        }

        try {
            setLoading(true);
            const payloadList: AuditCompanyFSCategoryService.AuditCompanyFSCategoryPayload[] = currSelected.map((fsId, idx) => ({
                companyId: Number(companyId),
                fsId: Number(fsId),
                statement: currStatement,
                aliasEn: '',
                aliasZh: '',
                parentFsIdOverride: null,
                displayOrder: idx + 1,
                isVisible: 1,
                isActive: 1,
            }));

            const res = await AuditCompanyFSCategoryService.batchReplace(Number(companyId), currStatement, payloadList);
            setSnackbar({open: true, severity: 'success', message: `Saved ${res?.length ?? 0} items successfully`});
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Save failed';
            setSnackbar({open: true, severity: 'error', message: msg});
        } finally {
            setLoading(false);
        }
    };

    const transferItemsBS = React.useMemo(() => mapFsTreeToTransfer(fsTreeBS), [fsTreeBS]);
    const transferItemsPL = React.useMemo(() => mapFsTreeToTransfer(fsTreePL), [fsTreePL]);
    const currEmpty = tab === 'BS'
        ? (!selectedFsIdsBS || selectedFsIdsBS.length === 0)
        : (!selectedFsIdsPL || selectedFsIdsPL.length === 0);

    return (
        <Box sx={{height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
            <Paper sx={{p: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 1, flexShrink: 0}} spacing={1}>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                        <Tab label="Balance Sheet Operation (BS)" value="BS" />
                        <Tab label="Profit & Loss Operation (PL)" value="PL" />
                    </Tabs>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{flexShrink: 0}}>
                        <TextField
                            size="small"
                            type="number"
                            label="Custom node limit"
                            value={customNodeQuota}
                            onChange={(e) => setCustomNodeQuota(Math.max(1, parseInt(e.target.value, 10) || 1))}
                            inputProps={{min: 1, step: 1}}
                            sx={{width: 160}}
                        />
                        <Button
                            variant="outlined"
                            onClick={handleSaveQuota}
                            disabled={quotaSaving || loading}
                            sx={{textTransform: 'none', fontWeight: 600}}
                        >
                            Save limit
                        </Button>
                        <Button variant="contained" color="secondary" onClick={handleSave} disabled={loading}
                                sx={{textTransform: 'none', fontWeight: 600}}>
                            Save
                        </Button>
                    </Stack>
                </Stack>

                <Typography variant="subtitle1" fontWeight={600} sx={{mb: 1, flexShrink: 0}}>
                    {tab === 'BS' ? 'Balance Sheet (BS)' : 'Profit & Loss Statement (PL)'}
                </Typography>

                <Box ref={transferHostRef} sx={{flex: 1, minHeight: 0}}>
                    {tab === 'BS' ? (
                        <TransferTreeList
                            items={transferItemsBS}
                            value={selectedFsIdsBS}
                            onChange={(next) => setSelectedFsIdsBS(next as number[])}
                            leftTitle="Available"
                            rightTitle="Selected"
                            tree
                            height={transferHeight}
                        />
                    ) : (
                        <TransferTreeList
                            items={transferItemsPL}
                            value={selectedFsIdsPL}
                            onChange={(next) => setSelectedFsIdsPL(next as number[])}
                            leftTitle="Available"
                            rightTitle="Selected"
                            tree
                            height={transferHeight}
                        />
                    )}
                </Box>

                <Box sx={{
                    mt: 1,
                    flexShrink: 0,
                    color: currEmpty ? theme.palette.error.main : 'inherit',
                    fontSize: 12,
                }}>
                    {currEmpty ? 'Please select at least one FS Category' : ' '}
                </Box>
            </Paper>

            <SnackbarField open={!!snackbar.open} message={snackbar.message || ''} severity={snackbar.severity}
                           onClose={() => setSnackbar(s => ({...s, open: false}))}/>
        </Box>
    );
};

export default CompanyFSCategoryOpera;
